import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { Compass, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const SEEN = "ledgerline-demo-tour-seen";
const onPath = (pathname, p) => (p === "/" ? pathname === "/" : pathname.startsWith(p));
const STEPS = [
  { path: "/", sel: '[data-testid="dash-title"]', title: "One family, one month end", body: "Everything is reported by month end. Pick any month at the top; every page and PDF follows it." },
  { path: "/", sel: '[data-testid="kpi-net-worth"]', title: "Combined net worth", body: "John D. and Emily S. Rockefeller's net worth, with every LLC, partnership and trust they own rolled up by percentage." },
  { path: "/", sel: '[data-testid="dash-family-charts"]', title: "Family asset mix and trend", body: "What the family owns, looking through every entity, and how net worth moved over the last six month ends." },
  { path: "/", sel: '[data-testid="dash-family-book"]', title: "Family statement book", body: "One click builds a single PDF with every net worth statement and entity valuation, ready to hand out." },
  { path: "/ownership-chart", sel: '[data-testid="org-canvas"]', title: "Who owns whom", body: "John and Emily each own 50% of Family Holdings and the Dynasty Trust, which split the energy, real estate and investment companies. Click any box to open its statement." },
  { path: "/reports/networth", sel: '[data-testid="statement-charts"]', title: "Net worth statements", body: "Each person and trust gets a statement with a look-through asset mix and a month-by-month trend." },
  { path: "/reports/networth", sel: '[data-testid="statement"]', title: "The statement itself", body: "Direct assets and liabilities, ownership interests valued through the chain, and a PDF download at the top." },
  { path: "/reports/entities", sel: '[data-testid="statement"]', title: "Entity valuations", body: "Every LLC and partnership has its own valuation report showing its owners and what each share is worth." },
];

function useTarget(sel, active) {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    if (!active) { setRect(null); return undefined; }
    let el = null;
    let tries = 0;
    const measure = () => { if (el) setRect(el.getBoundingClientRect()); };
    const find = setInterval(() => {
      el = document.querySelector(sel);
      if (el || ++tries > 40) {
        clearInterval(find);
        if (el) { el.scrollIntoView({ block: "center", behavior: "smooth" }); setTimeout(measure, 450); }
        else setRect(null);
      }
    }, 150);
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => { clearInterval(find); window.removeEventListener("scroll", measure, true); window.removeEventListener("resize", measure); };
  }, [sel, active]);
  return rect;
}

function cardStyle(r) {
  if (window.innerWidth < 640 || !r) return { left: 16, right: 16, bottom: 16 };
  const left = Math.min(Math.max(16, r.left), window.innerWidth - 400);
  if (r.bottom + 230 < window.innerHeight) return { left, top: r.bottom + 14 };
  if (r.top > 250) return { left, bottom: window.innerHeight - r.top + 14 };
  return { right: 24, bottom: 24 };
}

export const DemoTour = () => {
  const [step, setStep] = useState(null);
  const nav = useNavigate();
  const loc = useLocation();
  const s = step === null ? null : STEPS[step];
  const rect = useTarget(s?.sel, !!s && onPath(loc.pathname, s.path));

  const go = useCallback((i) => { setStep(i); if (i !== null && !onPath(loc.pathname, STEPS[i].path)) nav(STEPS[i].path); }, [loc.pathname, nav]);
  const close = () => { localStorage.setItem(SEEN, "1"); setStep(null); };
  useEffect(() => { if (!localStorage.getItem(SEEN)) go(0); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (step === null) return undefined;
    const k = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [step]);

  if (!s) {
    return (
      <Button variant="outline" size="sm" className="rounded-sm" onClick={() => go(0)} data-testid="tour-start">
        <Compass className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Demo tour</span>
      </Button>
    );
  }
  const pad = 8;
  const hl = rect && { top: Math.max(rect.top - pad, 4), left: rect.left - pad, width: rect.width + pad * 2, height: Math.min(rect.height + pad * 2, window.innerHeight - Math.max(rect.top - pad, 4) - 4) };
  return createPortal(
    <div className="fixed inset-0 z-[60]" data-testid="tour-overlay">
      <div className="absolute inset-0" onClick={close} />
      {hl ? (
        <div className="absolute rounded-sm ring-2 ring-amber-400 pointer-events-none transition-[top,left,width,height] duration-300"
          style={{ ...hl, boxShadow: "0 0 0 9999px rgba(15,23,42,0.55)" }} data-testid="tour-highlight" />
      ) : <div className="absolute inset-0 bg-slate-900/55 pointer-events-none" />}
      <div className="absolute w-auto sm:w-[380px] bg-white border border-slate-200 shadow-2xl rounded-sm p-5 fade-up" style={cardStyle(rect)} data-testid="tour-card">
        <div className="flex items-start justify-between gap-3">
          <div className="small-label">Step {step + 1} of {STEPS.length}</div>
          <button onClick={close} className="text-slate-400 hover:text-slate-700 transition-colors duration-200" data-testid="tour-close"><X className="h-4 w-4" /></button>
        </div>
        <h3 className="font-serif text-2xl mt-2" data-testid="tour-title">{s.title}</h3>
        <p className="text-sm text-slate-600 mt-2" data-testid="tour-body">{s.body}</p>
        <div className="flex items-center gap-2 mt-5">
          <div className="flex gap-1 mr-auto">{STEPS.map((_, i) => <span key={i} className={`h-1.5 w-4 rounded-full transition-colors duration-200 ${i === step ? "bg-amber-500" : "bg-slate-200"}`} />)}</div>
          {step > 0 && <Button variant="ghost" size="sm" onClick={() => go(step - 1)} data-testid="tour-back">Back</Button>}
          {step < STEPS.length - 1
            ? <Button size="sm" className="rounded-sm" onClick={() => go(step + 1)} data-testid="tour-next">Next</Button>
            : <Button size="sm" className="rounded-sm" onClick={close} data-testid="tour-finish">Finish</Button>}
        </div>
      </div>
    </div>,
    document.body
  );
};
